import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { useBrowseFilters } from '../../application/cars/browseState';
import { countActiveFilters } from '../../domain/cars/carFilters';
import { splitLocalePath } from '../../domain/i18n/locales';
import { ROUTE_PATHS } from '../../app/routes';
import GarageFilterSheet from '../cars/GarageFilterSheet';
import AppShareButton from '../pwa/AppShareButton';
import { SlidersIcon } from '../icons/ControlIcons';
import { BrandWordmark } from './BrandMark';

/**
 * The header the tabbed screens wear: the lockup, and the one control on this
 * app that is not a car.
 *
 * The lockup is a LINK HOME rather than a button that opens something. The
 * sibling product hangs a settings drawer off its wordmark because it has a
 * drawer to hang; this app has no settings, no account and one language, so a
 * lockup that opened a panel would be a panel invented to justify a tap.
 *
 * The FILTER AFFORDANCE, and why it is here rather than only above the grid: the
 * header is sticky and the garage's pill row scrolls away with the cards. A
 * reader eight cards down who wants to narrow the shelf would otherwise have to
 * scroll back to the top to reach a control. The row is how you take one filter
 * off while you can see it; this is how you reach the whole model from anywhere.
 *
 * They are not two features to keep in step — they are two drawings of the same
 * state, and the state is the URL (`browseState`). That is also why this
 * component may read a garage hook without becoming a garage component: it reads
 * the ADDRESS BAR, which is chrome's business, and it does so only to say how
 * many filters are on in the control's accessible name. "Filters" and "Filters,
 * 2 active" are different states, and a coloured tint is not a state anybody
 * reading by ear can perceive (NFR-04).
 *
 * The sheet is mounted only while open. A `<dialog>` that is always in the tree
 * is a focus-order surprise waiting for somebody with a keyboard, and unmounting
 * is what makes it re-read the catalogue on every open.
 *
 * The control is drawn only on the garage. Today that is the only tabbed screen
 * there is; the day Rankings arrives, a filter button over a screen with nothing
 * to filter would be a control that opens an empty sheet.
 */
export default function AppHeader() {
    const { localeCode } = useLocale();
    const t = useTranslate();
    const { pathname } = useLocation();
    const { filters } = useBrowseFilters();
    const [isFilterSheetOpen, setFilterSheetOpen] = useState(false);
    const activeCount = countActiveFilters(filters);
    const isGarage = splitLocalePath(pathname).routePath === ROUTE_PATHS.garage;

    return (
        <>
            <header className="brand-header">
                <Link
                    className="brand-header__lockup"
                    to={`/${localeCode}${ROUTE_PATHS.garage}`}
                    aria-label={t('a11y.brandHome')}
                >
                    {/* Decorative here: the link already carries the product's
                        name, and announcing the wordmark too would say
                        "Legends" twice in a row. */}
                    <BrandWordmark className="brand-header__wordmark" sizes="132px" alt="" />
                </Link>

                {/* Both controls belong to the garage. On a car the header is
                    not drawn at all — that route is immersive and carries its
                    own back button and its own share action. */}
                {isGarage ? <AppShareButton /> : null}

                {isGarage ? (
                    <button
                        type="button"
                        className={`brand-header__filters${activeCount > 0 ? ' brand-header__filters--active' : ''}`}
                        onClick={() => setFilterSheetOpen(true)}
                        aria-haspopup="dialog"
                        aria-label={activeCount > 0
                            ? t('garage.filter.openWithCount', { count: activeCount })
                            : t('garage.filter.open')}
                    >
                        <SlidersIcon size={22} />
                    </button>
                ) : null}
            </header>

            {isFilterSheetOpen ? <GarageFilterSheet onClose={() => setFilterSheetOpen(false)} /> : null}
        </>
    );
}
